import React from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import deepFind, { returnMultiAnswer } from "../../utils/deepFind";
import { Button } from "../Button";
import { Link } from "react-router-dom";
import { TimeAgo } from "../TimeAgo";
import capitalizeText from "../../utils/capitalizeText";
import icons from "../../constants/icons";
import { getStorage } from "../../utils/storage";
import { connect } from "react-redux";
import ButtonToClipboard from "../Button/ButtonToClipboard";
import userPhotoPlaceholder from "../../assets/userPhotoPlaceholder.png";
import language from "../../language";
import formatDate from "../../utils/formatDate";
const ACTION_COLORS = {
  view: "#0d6efd",
  detail: "#0d6efd",
  show: "#0d6efd",
  print: "#0d6efd",
  approve: "#28a745",
  accept: "#28a745",
  confirm: "#28a745",
  receive: "#28a745",
  complete: "#28a745",
  unblock: "#28a745",
  delete: "#dc3545",
  reject: "#dc3545",
  cancel: "#dc3545",
  remove: "#dc3545",
  block: "#dc3545",
  edit: "#fd7e14",
  update: "#fd7e14",
  modify: "#fd7e14",
  transfer: "#6f42c1",
  ship: "#6f42c1",
};

const ACTION_ICONS = {
  view: "visibility",
  detail: "visibility",
  show: "visibility",
  print: "print",
  approve: "check_circle",
  accept: "check_circle",
  confirm: "done",
  receive: "done",
  complete: "task_alt",
  unblock: "lock_open",
  delete: "delete",
  reject: "cancel",
  cancel: "cancel",
  remove: "remove_circle",
  block: "block",
  edit: "edit",
  update: "edit",
  modify: "edit",
  transfer: "swap_horiz",
  ship: "local_shipping",
};

function getActionColor(name = "") {
  const n = (name || "").toLowerCase();
  for (const [keyword, color] of Object.entries(ACTION_COLORS)) {
    if (n.includes(keyword)) return color;
  }
  return "#6c757d";
}

function getActionIcon(name = "") {
  const n = (name || "").toLowerCase();
  for (const [keyword, icon] of Object.entries(ACTION_ICONS)) {
    if (n.includes(keyword)) return icon;
  }
  return "more_horiz";
}

const MENU_ESTIMATED_HEIGHT = 180; // ~42px per item × 4 items + padding
const MENU_GAP = 6;

class Row extends React.PureComponent {
  state = {
    user: {},
    dropdownOpen: false,
    menuStyle: {},
    opensUpward: false,
  };

  btnRef = React.createRef();
  menuRef = React.createRef();

  handleOutsideClick = (e) => {
    const onBtn  = this.btnRef.current  && this.btnRef.current.contains(e.target);
    const onMenu = this.menuRef.current && this.menuRef.current.contains(e.target);
    if (!onBtn && !onMenu) {
      this.setState({ dropdownOpen: false });
    }
  };

  toggleDropdown = () => {
    if (this.state.dropdownOpen) {
      this.setState({ dropdownOpen: false });
      return;
    }
    if (!this.btnRef.current) return;

    const rect = this.btnRef.current.getBoundingClientRect();
    const menuRight = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;

    // Prefer opening downward; flip upward only when not enough room below
    const opensUpward = spaceBelow < MENU_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

    const menuStyle = {
      position: "fixed",
      right: menuRight,
      zIndex: 9999,
      ...(opensUpward
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
    };

    this.setState({ dropdownOpen: true, menuStyle, opensUpward });
  };

  resolveHeaderValue = (item, header = {}) => {
    const fallbackKeys = [
      header.fallbackKey,
      ...(Array.isArray(header.fallbackKeys) ? header.fallbackKeys : []),
    ].filter(Boolean);

    let value = deepFind(item, header.key);

    if (value === undefined || value === null || value === "") {
      for (const fallbackKey of fallbackKeys) {
        const fallbackValue = deepFind(item, fallbackKey);

        if (
          fallbackValue !== undefined &&
          fallbackValue !== null &&
          fallbackValue !== ""
        ) {
          value = fallbackValue;
          break;
        }
      }
    }

    return value;
  };

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });
    document.addEventListener("mousedown", this.handleOutsideClick);
  };

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }

  getVisibleHeaderActions = (actions = [], item) => {
    return actions.filter((action) => {
      if (!action) return false;
      if (action.canAccess === false) return false;
      if (typeof action.isHidden === "function" && action.isHidden(item)) {
        return false;
      }
      if (typeof action.isHidden !== "function" && action.isHidden) {
        return false;
      }
      return true;
    });
  };

  getVisibleDropdownActions = (actions = [], item) => {
    return actions.filter((action) => {
      if (!action) return false;
      if (!action.canAccess) return false;
      if (typeof action.isHidden === "function" && action.isHidden(item)) {
        return false;
      }
      if (typeof action.isHidden !== "function" && action.isHidden) {
        return false;
      }
      return true;
    });
  };

  renderInlineAction = (action, item, keyRowIndex, key) => {
    const actionText = action.text || action.name || action.title || "";
    const normalizedActionText = `${actionText}`.trim().toLowerCase();
    const shouldRenderIconOnly = !!action.icon && !actionText;
    const shouldShowIcon = !!action.icon && !actionText;
    const semanticActionClassName =
      normalizedActionText === "delete" || normalizedActionText === "cancel"
        ? "table-inline-action-btn-solid"
        : "";
    const actionClassName = `btn-sm table-inline-action-btn ${
      shouldRenderIconOnly ? "table-inline-action-btn-icon-only" : ""
    } ${semanticActionClassName} ${action.className || ""}`.trim();

    if (action.route) {
      return (
        <Link
          key={key}
          className={`btn ${actionClassName}`}
          to={action.route}
          onClick={() => action.onPress && action.onPress(item, keyRowIndex)}
          title={action.title || actionText}
        >
          {shouldShowIcon && (
            <span className={`${process.env.REACT_APP_ICONS_TYPE} icon`}>
              {action.icon}
            </span>
          )}
          {!shouldRenderIconOnly && actionText}
        </Link>
      );
    }

    return (
      <Button
        key={key}
        className={actionClassName}
        icon={shouldShowIcon ? action.icon : undefined}
        onPress={() => action.onPress && action.onPress(item, keyRowIndex)}
        text={shouldRenderIconOnly ? "" : actionText}
        title={action.title || actionText}
      />
    );
  };

  render() {
    const {
      headers,
      rowPress,
      actions,
      item,
      keyRowIndex,
      invisibleLink,
    } = this.props;
    return (
      <tr key={keyRowIndex}>
        {headers &&
          headers.map((header, h) => {
            const nestedHeaders =
              Array.isArray(header.headers) && !header.combineHeader
                ? header.headers
                : null;
            const nestedSeparator =
              header.separator || header.concatSeparator || "";

            if (nestedHeaders?.length) {
              const nestedItems = nestedHeaders
                .map((nestedHeader) => {
                  const config =
                    typeof nestedHeader === "string"
                      ? { key: nestedHeader }
                      : nestedHeader || {};

                  let value = this.resolveHeaderValue(item, config);
                  if (value === undefined || value === null || value === "") {
                    return null;
                  }

                  if (config.isMoment) {
                    value = formatDate(value, config.formatTime || "ll", config.inputFormat || null);
                  }

                  return {
                    ...config,
                    value,
                  };
                })
                .filter(Boolean);

              return (
                <td key={h}>
                  {nestedItems.length === 0 ? (
                    <span>-</span>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {nestedItems.map((nestedItem, i) => {
                        const showSeparator = i < nestedItems.length - 1;
                        const separator =
                          nestedItem.separator || nestedSeparator;
                        return (
                          <React.Fragment key={i}>
                            {nestedItem.type === "countCheck" ? (
                              <div className="d-flex align-items-center gap-2">
                                {Array(Number(nestedItem.value))
                                  .fill()
                                  .map((_, countIndex) => {
                                    return (
                                      <div
                                        className={`status-icon-container success`}
                                        key={countIndex}
                                      >
                                        <span
                                          className={
                                            process.env.REACT_APP_ICONS_TYPE
                                          }
                                        >
                                          {icons.success}
                                        </span>
                                      </div>
                                    );
                                  })}
                              </div>
                            ) : (
                              <span
                                style={nestedItem.styles || {}}
                                className="text-overflow cell-width"
                              >
                                {nestedItem.isMoment
                                  ? `${nestedItem.value}`
                                  : capitalizeText(`${nestedItem.value}`)}
                              </span>
                            )}
                            {showSeparator && <span>{separator}</span>}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </td>
              );
            }

            let hasActionBtn =
              header.key === "action" || header.key === "multiple-actions";

            if (
              Object.keys(header).includes("checkValue") &&
              Object.keys(header).includes("checkKey") &&
              header.checkValue !== deepFind(item, header.checkKey)
            ) {
              hasActionBtn = false;
            }

            let find_text = this.resolveHeaderValue(item, header);
            find_text =
              find_text !== undefined && find_text !== null && find_text !== ""
                ? find_text
                : "-";
            let text =
              header.isMoment && find_text && find_text !== "-"
                ? formatDate(find_text, header.formatTime, header.inputFormat || null)
                : `${find_text} `;

            return (
              <React.Fragment key={h}>
                {hasActionBtn ? (
                  <td key={h} className={["table-sticky-action", header.className || ""].filter(Boolean).join(" ")}>
                    {header.key === "multiple-actions" ? (
                      <div className="table-raw-multiple-actions">
                        {this.getVisibleHeaderActions(header.actions, item).map(
                          (btn, i) => (
                            <div
                              key={i}
                              className="table-raw-multiple-actions-button"
                              title={btn.title}
                            >
                              <Button
                                className={`btn-sm ${btn.className || "btn-transparent"}`}
                                icon={btn.icon}
                                onPress={() => btn.onPress(item)}
                                text={btn.text || btn.name || btn.title || ""}
                              />
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      (() => {
                        const visibleActions = this.getVisibleDropdownActions(actions, item);
                        if (visibleActions.length === 0) return null;
                        return (
                          <div className="table-action-dots-wrapper">
                            <button
                              ref={this.btnRef}
                              className="table-action-dots-btn"
                              onClick={this.toggleDropdown}
                              title="Actions"
                            >
                              <span className={process.env.REACT_APP_ICONS_TYPE}>more_vert</span>
                            </button>
                            {this.state.dropdownOpen && ReactDOM.createPortal(
                              <div
                                ref={this.menuRef}
                                className={`table-action-dots-menu ${this.state.opensUpward ? "opens-up" : "opens-down"}`}
                                style={this.state.menuStyle}
                              >
                                {visibleActions.map((action, a) => {
                                  const actionName = action.text || action.name || action.title || "";
                                  const color = getActionColor(actionName);
                                  const icon = getActionIcon(actionName);
                                  return (
                                    <button
                                      key={a}
                                      className="table-action-dots-item"
                                      onClick={() => {
                                        this.setState({ dropdownOpen: false });
                                        action.onPress && action.onPress(item, keyRowIndex);
                                      }}
                                    >
                                      <span
                                        className="table-action-dots-icon"
                                        style={{ backgroundColor: color }}
                                      >
                                        <span className={process.env.REACT_APP_ICONS_TYPE}>
                                          {icon}
                                        </span>
                                      </span>
                                      <span className="table-action-dots-label">{actionName}</span>
                                    </button>
                                  );
                                })}
                              </div>,
                              document.body
                            )}
                          </div>
                        );
                      })()
                    )}
                  </td>
                ) : (
                  <td key={h} className={header.className || ""}>
                    {header.type === "numbering" ? (
                      <div>
                        <span>{this.props.count}</span>
                      </div>
                    ) : header.type === "labResult" ? (
                      <Button
                        text={language[this.props.defaultLanguage].view}
                        onPress={() => header.onPress(item)}
                      />
                    ) : header.type === "photo" ? (
                      <div>
                        <img
                          alt=""
                          onClick={() =>
                            header.onPress ? header.onPress(item) : null
                          }
                          src={
                            text !== "- "
                              ? `data:image/png;base64,${text}`
                              : userPhotoPlaceholder
                          }
                          style={{
                            width: 45,
                            height: 45,
                            borderRadius: "50%",
                            aspectRatio: 3 / 2,
                            border: "2px solid var(--light-color)",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        />
                      </div>
                    ) : header.type === "link" ? (
                      <div>
                        <div
                          className={`d-flex align-items-center tableLinkContainer ${
                            invisibleLink ? "invisible" : ""
                          }`}
                        >
                          <Link
                            className="text-primary text-hover-decoration text-overflow cell-width"
                            onClick={() =>
                              header.onPress
                                ? header.onPress(item, keyRowIndex)
                                : null
                            }
                            to="#"
                            title={!returnMultiAnswer(item, header.key) && text}
                            style={header.styles || {}}
                          >
                            {capitalizeText(text) + ""}
                          </Link>

                          <div className="copyTableData">
                            {header.copyEnabled && (
                              <ButtonToClipboard textToCopy={text} />
                            )}
                          </div>
                        </div>
                      </div>
                    ) : header.type === "timeAgo" ? (
                      <div className="text-overflow cell-width">
                        {text.replaceAll(" ", "") === "-" ? (
                          <span>-</span>
                        ) : (
                          <TimeAgo
                            date={moment(text.replaceAll(" ", "")).format(
                              "lll",
                            )}
                          />
                        )}
                      </div>
                    ) : header.type === "status" ? (
                      <div
                        className={`badge badge-default badge-${text.toLowerCase()}`}
                      >
                        <span>{capitalizeText(text) + ""}</span>
                      </div>
                    ) : header.type === "countCheck" && Number(text) ? (
                      <div className="d-flex align-items-center gap-2">
                        {Array(Number(text))
                          .fill()
                          .map((_, i) => {
                            return (
                              <div
                                className={`status-icon-container success`}
                                key={i}
                              >
                                <span
                                  className={process.env.REACT_APP_ICONS_TYPE}
                                >
                                  {icons.success}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    ) : header.type === "testLabResult" &&
                      text?.trim()?.toUpperCase() === "NEGATIVE" ? (
                      <span style={{ color: "red", ...(header.styles || {}) }}>
                        {text}
                      </span>
                    ) : header.render ? (
                      <span className="text-overflow cell-width">
                        {header.render(item)}
                      </span>
                    ) : (
                      <div
                        onClick={() =>
                          header.key !== "action" && rowPress
                            ? rowPress(item, keyRowIndex)
                            : null
                        }
                        title={!returnMultiAnswer(item, header.key) && text}
                      >
                        {returnMultiAnswer(item, header.key) ? (
                          returnMultiAnswer(item, header.key)
                        ) : text?.replace(" ", "")?.toLowerCase() === "true" ? (
                          <div className={`status-icon-container success`}>
                            <span className={process.env.REACT_APP_ICONS_TYPE}>
                              {icons.success}
                            </span>
                          </div>
                        ) : text?.replaceAll(" ", "")?.toLowerCase() ===
                          "false" ? (
                          <div className={`status-icon-container failed`}>
                            <span className={process.env.REACT_APP_ICONS_TYPE}>
                              {icons.fail}
                            </span>
                          </div>
                        ) : (
                          <span
                            style={header.styles || {}}
                            className="text-overflow cell-width"
                          >
                            {header.isMoment ? text : capitalizeText(text) + ""}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </React.Fragment>
            );
          })}
      </tr>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(Row);